import { track } from "./tracker";
import { EVENTS } from "./events";

/**
 * A call-to-action click.
 *
 * `cta_id` is a stable slug that identifies WHAT the button does and MUST NOT
 * change when the visible copy changes (e.g. `become_a_creator` stays the same
 * whether the label reads "Become a Creator" or "Join the team"). `location`
 * identifies WHERE on the site it was clicked so the same CTA can be compared
 * across placements (e.g. the "become_a_creator" button in `header` vs
 * `member_lead_form` vs `footer`). `destination` is the route or URL it leads
 * to (null for in-page scroll actions).
 */
export interface CtaMeta {
  cta_id: string;
  location: string;
  destination?: string | null;
}

/**
 * Fire a `cta_click` event to both sinks (GA4 + Datadog RUM) via the shared
 * tracker, so every CTA carries the same first-touch / utm / visitor / variant
 * enrichment as our form events. Use this directly inside an existing `onClick`
 * handler; for plain navigation links prefer the `<TrackedLink>` /
 * `<TrackedAnchor>` wrappers, which call this for you.
 */
export function trackCta(meta: CtaMeta, extra: Record<string, unknown> = {}): void {
  track(EVENTS.CTA_CLICK, {
    cta_id: meta.cta_id,
    location: meta.location,
    destination: meta.destination ?? null,
    ...extra,
  });
}

/**
 * Derive a stable `cta_id` from a route path, for the few CTAs whose
 * destination is configurable per page (e.g. the header nav link). Prefer an
 * explicit literal `cta_id` at fixed call sites. `/become-a-creator` →
 * `become_a_creator`; `/` → `home`.
 */
export function ctaIdFromPath(path: string): string {
  const slug = path.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug || "home";
}
